import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, { Icons as GridIcons } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Room } from "../../../../../../../models/room";
import { Row } from "../../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import RoomModal from "./RoomModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const RoomList = ({ permission }: Props) => {
  const { currentUser } = useAppState();
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const {
    page,
    pageSize,
    total,
    hasErrors,
    rooms,
    isLoading,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createRoom,
    updateRoom,
  } = useActions();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRoom, setSelectedRoom] = useState<Room>();
  const [rows, setRows] = useState<Row[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
        `}
    >
      {status}
    </Badge>
  );

  const editRoom = (index: number) => {
    const roomToUpdate = rooms.find((room) => Number(room.id) === Number(index));

    if (roomToUpdate) {
      setSelectedRoom(roomToUpdate);
      if (hasErrors !== undefined) setHasErrors(undefined);
      setOpenModal(true);
    }
  };

  const changeStatusRoom = (index: number) => {
    const roomToUpdate = rooms.find((room) => Number(room.id) === Number(index));

    if (roomToUpdate) {
      updateRoom({
        id: Number(index),
        room: {
          ...roomToUpdate,
          status: roomToUpdate.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveLocationSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, rooms);

    setRows([...rowsSelected]);
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request-notification-success-room-changes",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="roomsListTable">
      <ShipeeziNewGrid
        data={rooms}
        columns={[
          { name: "code", title: translate("code") },
          { name: "name", title: translate("name") },
          {
            name: "status",
            title: translate("status"),
            formatComponent: (status: string) => badgeStatus(status),
            hideSearch: true,
          },
        ]}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (roomId) => editRoom(Number(roomId))
            : undefined,
          handleChange: (roomId) => changeStatusRoom(Number(roomId)),
        }}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveLocationSelected(rows)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleChangeStatus", "handleNewRecord"],
          },
          handleNewRecord: permission && isShowAction(currentUser, permission.create)
            ? {
              text: translate("create"),
              action: () => {
                setSelectedRoom(undefined);
                if (hasErrors !== undefined) setHasErrors(undefined);
                setOpenModal(true);
              },
              tooltip: translate("create"),
            }
            : undefined,
          handleSearch: {
            onSearch: handleSearch,
          },
          handleFilterBy: {
            categories: [
              { name: translate("code"), column: "code" },
              { name: translate("name"), column: "name" },
              {
                name: translate("status"),
                column: "status",
                options: [Status.ACTIVE, Status.INACTIVE],
              },
            ],
            onChange: handleFilter,
          },
          handleChangeStatus: renderButtonToChangeStatus(),
        }}
      />
      {openModal && (
        <RoomModal
          roomToUpdate={selectedRoom}
          hasError={hasErrors}
          openModal={openModal}
          isLoading={isLoading}
          closeModal={() => setOpenModal(false)}
          submitRoom={(room: Room) => {
            if (selectedRoom && selectedRoom.id)
              updateRoom({
                id: selectedRoom.id,
                room: { ...selectedRoom, ...room },
              });
            else
              createRoom({
                ...room,
                companyId: currentUser?.companyId,
              });
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateRoom({ id: rowId, room: { status } as Room });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default RoomList;
